import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./UpdatePassword.css";
import TransLoader from "../../loader/TransLoader";
import CustomAlert from "../../custom_alert/CustomAlert";

export default function UpdatePassword() {
  const [config, setConfig] = useState(null);
  const [empCode, setEmpCode] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const navigate = useNavigate();

  // Load config and employee ID
  useEffect(() => {
    setEmpCode(sessionStorage.getItem("user_id"));
    fetch("/config.json")
      .then((res) => res.json())
      .then((data) => setConfig(data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (empCode && config) setLoading(false);
  }, [empCode, config]);

  // -----------------------
  // Logout function
  // -----------------------
  const handleLogout = useCallback(
    async (type = "manual", reason = "Logged out") => {
      setAlertMessage(reason);

      // Clear frontend storage
      sessionStorage.clear();
      localStorage.clear();

      // Call backend logout API
      if (config) {
        try {
          const response = await fetch(`${config.BASE_URL}${config.LOGOUT}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ logout_type: type }),
          });

          const data = await response.json();
          if (data.status === "success") {
            console.log("Logout successful");
          } else {
            console.warn("Logout failed:", data.message);
          }
        } catch (err) {
          console.error("Logout request failed:", err);
        }
      }

      // Redirect after short delay to allow alert display
      setTimeout(() => {
        navigate("/", { state: { logoutType: type, message: reason } });
      }, 2000); // shorter delay for UX
    },
    [config, navigate]
  );

  // -----------------------
  // Password update handler
  // -----------------------
  const validateInputs = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setAlertMessage("All fields are mandatory.");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setAlertMessage("New password and confirm password do not match.");
      return false;
    }
    if (newPassword.length < 6) {
      setAlertMessage("New password must be at least 6 characters.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;
    if (!config || !empCode) return;

    setLoading(true);

    try {
      const res = await fetch(`${config.BASE_URL}${config.UPDATE_PASSWORD}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "RC_EMP_NEW",
        },
        body: JSON.stringify({
          emp_code: empCode,
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setAlertMessage("Password updated successfully! Logging out...");
        setTimeout(() => {
          handleLogout("password_change", "Password changed successfully");
        }, 2000);
      } else {
        setAlertMessage(data.message || "Password update failed.");
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Network error while updating password.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <TransLoader />;

  return (
    <>
      <div className="content-card">
        <div className="passbox" >


        
        <h3>Update Password</h3>

        {/* Current Password */}
        <div className="password-field">
          <label>Current Password:</label>
          <div className="input-wrapper">
            <input
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <button type="button" onClick={() => setShowCurrent(!showCurrent)}>
              {showCurrent ? "😎" : "🧐"}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div className="password-field">
          <label>New Password:</label>
          <div className="input-wrapper">
            <input
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button type="button" onClick={() => setShowNew(!showNew)}>
              {showNew ? "😎" : "🧐"}
            </button>
          </div>
        </div>

        {/* Confirm New Password */}
        <div className="password-field">
          <label>Confirm New Password:</label>
          <div className="input-wrapper">
            <input
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)}>
              {showConfirm ? "😎" : "🧐"}
            </button>
          </div>
        </div>

        <button className="add-update-btn" onClick={handleSubmit}>
          Update Password
        </button>
      </div>
      </div>

      <CustomAlert message={alertMessage} onClose={() => setAlertMessage("")} />
    </>
  );
}
